import * as R from 'ramda'
// React
import { useRef } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { HallType } from '@weblite/ts-action/hall'
// Logic
import { useHall } from '$logic/hall/react-hook'
import {
  COURSE_VITRINE_TAG,
  DISABLE_WATERMARK_TAG,
  HIDE_MEMBER_COUNT_TAG,
  ISSUE_TAG,
  SUPER_ROOM_TAG,
  VOD_ACTIVATED_TAG,
} from '$logic/hall/constant'
import { useRoom } from '$logic/room/react-hook'
import { changeHallInfo } from '$logic/hall'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { closeHallSettings } from '$logic/router'
// Common
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
// Localization
import { areEqualStr } from '$func/text'
import { t } from '$i18n'
import { AppBarBackTitle } from '$common/app-bar/app-bar-back-title'
// Component
import { HallSettingsActions } from './hall-settings-actions'
import {
  HallSettingsProfile,
  HallSettingsProfileApiType,
} from './hall-settings-profile'
import { HallSettingsCriticalActions } from './hall-settings-critical-actions'
import {
  EditHallAdvancedSettings,
  HallAdvancedSettingsRefApi,
} from './edit-hall-advanced-settings'
import { HallSettingsOptions } from './hall-settings-options'
import { HallSettingsSuperUserActions } from '$comp/_app/components/leftPanel/components/hall-settings/hall-settings-superuser-actions'
import { useWorkspaceColorByRoomId } from '$logic/workspace'

interface Props {
  roomId: string
}

export function HallSettings({ roomId }: Props) {
  const { entity: hall } = useHall(roomId)
  const profileRef = useRef<HallSettingsProfileApiType>(null)
  const advancedSettingsRef = useRef<HallAdvancedSettingsRefApi>(null)
  const room = useRoom(roomId)
  const permissions = useClientUserPermissions(roomId)

  const backgroundColor = useWorkspaceColorByRoomId(roomId)
  // TODO: show skeleton if needed
  if (!hall) return null

  const isGroup = room?.type === 'GROUP'
  const isChannel = room?.type === 'CHANNEL'
  const displayType = isGroup
    ? t('hallSetting.group')
    : t('hallSetting.channel')

  const onConfirmHandler = async () => {
    const { current: profileApi } = profileRef
    const { current: advancedApi } = advancedSettingsRef
    if (!profileApi || (isChannel && !advancedApi)) return

    const name = profileApi.getName()
    const description = profileApi.getDescription()
    const image = profileApi.getImage()
    const courseVitrineEnabled = advancedApi?.getCourseVitrine?.()
    const issueEnabled = advancedApi?.getIssue?.()
    const protectContent = advancedApi?.getProtectContent?.()
    const vodActivated = advancedApi?.getVodActivated?.()
    const hideMemberCount = advancedApi?.getHideMemberCount?.()
    const watermarkDisabled = advancedApi?.getWatermarkDisabled?.()
    const superRoomEnabled = advancedApi?.getSuperRoom?.()

    const previousCourseVitrineEnabled =
      hall?.tags?.includes(COURSE_VITRINE_TAG)
    const previousIssueEnabled = hall?.tags?.includes(ISSUE_TAG)
    const previousVodActivated = hall?.tags?.includes(VOD_ACTIVATED_TAG)
    const previousProtectContentEnabled = hall?.protectContent
    const previousHideMemberCount = hall?.tags?.includes(HIDE_MEMBER_COUNT_TAG)
    const previousWatermarkDisabled = hall?.tags?.includes(
      DISABLE_WATERMARK_TAG,
    )
    const previousSuperRoomEnabled = hall?.tags?.includes(SUPER_ROOM_TAG)

    const areTagsChanged =
      courseVitrineEnabled !== previousCourseVitrineEnabled ||
      issueEnabled !== previousIssueEnabled ||
      previousVodActivated !== vodActivated ||
      previousHideMemberCount !== hideMemberCount ||
      previousWatermarkDisabled !== watermarkDisabled ||
      previousSuperRoomEnabled !== superRoomEnabled

    const patch = {
      ...(!areEqualStr(description, hall.description) && { description }),
      ...(!areEqualStr(name, hall.name) && { name }),
      ...(!areEqualStr(image, hall.image) && { image }),
      ...(isChannel && areTagsChanged && { tags: advancedApi?.getTags?.() }),
      ...(isChannel &&
        protectContent !== previousProtectContentEnabled && {
          protectContent,
        }),
    }

    if (!R.isEmpty(patch)) {
      showSaveChangesLoadingModal()
      await changeHallInfo(roomId, patch)
      await closeCurrentModal()
    }
    await closeHallSettings()
  }

  return (
    <PageWithAppBar
      useCustomScroller
      appBar={
        permissions?.changeHallInfo ? (
          <AppBarBackTitleConfirm
            title={displayType}
            onConfirm={onConfirmHandler}
            onBack={closeHallSettings}
            backgroundColor={backgroundColor}
          />
        ) : (
          <AppBarBackTitle
            title={displayType}
            onBack={closeHallSettings}
            backgroundColor={backgroundColor}
          />
        )
      }
    >
      <SettingsSection>
        <HallSettingsProfile
          hall={hall}
          ref={profileRef}
          editable={Boolean(permissions?.changeHallInfo)}
          isGroup={isGroup}
        />
      </SettingsSection>

      {isChannel && (
        <SettingsSection>
          <EditHallAdvancedSettings hall={hall} ref={advancedSettingsRef} />
        </SettingsSection>
      )}

      {permissions?.changeHallInfo && (
        <SettingsSection>
          <HallSettingsOptions hall={hall} isGroup={isGroup} />
        </SettingsSection>
      )}

      {permissions?.isAdmin && (
        <SettingsSection>
          <HallSettingsActions
            isGroup={isGroup}
            hallId={roomId}
            permissions={permissions}
          />
        </SettingsSection>
      )}

      {permissions?.isOwner && (
        <SettingsSection>
          <HallSettingsCriticalActions
            hall={hall}
            hallType={room?.type as HallType}
          />
        </SettingsSection>
      )}

      {permissions?.isSuperUser && (
        <SettingsSection headerText="SUPER USER ACTIONS" headerColor="red">
          <HallSettingsSuperUserActions hall={hall} room={room} />
        </SettingsSection>
      )}
    </PageWithAppBar>
  )
}
